import { Plate } from '../components/Card';
import React, { useEffect, useState } from 'react';
import api from '../api';

const fmt = v => new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(v || 0);

const fmtCash = v => {
  const abs = new Intl.NumberFormat('ru-RU', { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(Math.abs(v || 0));
  if ((v || 0) > 0) return '+' + abs;
  if ((v || 0) < 0) return '−' + abs;
  return abs;
};

const cashColor = v => (v || 0) > 0 ? '#0e9f6e' : (v || 0) < 0 ? '#e04848' : 'var(--text2)';

function getStoredUser() {
  try {
    const raw = localStorage.getItem('auth_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function getProjection(turnover, period) {
  if (!period || !turnover) return null;
  const [year, month] = period.split('-').map(Number);
  const daysInMonth = new Date(year, month, 0).getDate();
  const now = new Date();
  const isCurrentMonth = year === now.getFullYear() && month === (now.getMonth() + 1);
  if (!isCurrentMonth) return null;
  const daysPassed = now.getDate();
  if (daysPassed === 0) return null;
  const dailyAvg = turnover / daysPassed;
  const projection = Math.round(dailyAvg * daysInMonth);
  return {
    projection,
    dailyAvg,
    daysPassed,
    daysInMonth,
    projRoyalty: Math.round(projection * 0.05),
    projMarketing: Math.round(projection * 0.05),
  };
}

const COLORS = ['#00a7dc','#0e9f6e','#e08a00','#e04848','#00a7dc','#00a7dc','#00a7dc','#0e9f6e','#e08a00','#0e9f6e','#00a7dc','#e08a00'];

const StatusBadge = ({ debt }) => {
  if (debt <= 0) return (
    <span style={{ background: '#0e9f6e20', color: '#0e9f6e', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
      ✓ Оплачено
    </span>
  );
  if (debt < 10000) return (
    <span style={{ background: '#e08a0020', color: '#e08a00', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
      ⚠ Частично
    </span>
  );
  return (
    <span style={{ background: '#e0484820', color: '#e04848', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
      ✗ Долг
    </span>
  );
};

function buildInvoices(partners) {
  const invoices = [];
  partners.forEach(p => {
    const items = [
      { name: 'Роялти 5%', expected: p.royalty_expected, paid: p.royalty_paid },
      { name: 'Маркетинг 5%', expected: p.marketing_expected, paid: p.marketing_paid },
      { name: 'Аренда оборудования', expected: p.equipment_rent, paid: p.equipment_rent_paid },
      { name: 'Аренда помещения', expected: p.space_rent, paid: p.space_rent_paid },
      { name: 'Паушальный взнос', expected: p.franchise_fee, paid: p.franchise_fee_paid },
      { name: 'МойСклад', expected: p.moysklad_fee, paid: p.moysklad_fee_paid },
      { name: 'Связь', expected: p.comm_fee, paid: p.comm_fee_paid },
      { name: 'Бухгалтерия', expected: p.accounting_fee, paid: p.accounting_fee_paid },
      { name: 'Выдача карт', expected: p.card_fee, paid: p.card_fee_paid },
      { name: 'Bitrix24', expected: p.batrek_fee, paid: p.batrek_fee_paid },
      { name: 'Телематика', expected: p.telematics_fee, paid: p.telematics_fee_paid },
    ].filter(item => item.expected > 0);

    items.forEach(item => {
      let status = 'unpaid';
      if (item.paid >= item.expected) status = 'paid';
      else if (item.paid > 0) status = 'partial';
      invoices.push({
        id: `${p.id}-${item.name}`,
        partner: p.name,
        name: item.name,
        amount: item.expected,
        paid: item.paid,
        status,
      });
    });
  });
  return invoices;
}

export default function PartnerPortal() {
  const [activeTab, setActiveTab] = useState('branches');
  const [partners, setPartners] = useState([]);
  const [prevPartnerMap, setPrevPartnerMap] = useState({});
  const [periods, setPeriods] = useState([]);
  const [period, setPeriod] = useState('');
  const [loading, setLoading] = useState(true);
  const [liveStats, setLiveStats] = useState({});
  const [dailyStats, setDailyStats] = useState({});
  const [storeDayIdx, setStoreDayIdx] = useState({});
  const [summary, setSummary] = useState(null);
  const [debtFilter, setDebtFilter] = useState('all');
  const [portalUsers, setPortalUsers] = useState([]);
  const [branchAccess, setBranchAccess] = useState({});
  const [accessLoading, setAccessLoading] = useState(false);
  const currentUser = getStoredUser();
  const canManageAccess = ['admin', 'manager'].includes(currentUser?.role);

  useEffect(() => {
    api.get('/fdm/periods').then(r => {
      const ps = r.data;
      setPeriods(ps);
      if (ps.length) setPeriod(ps[0]);
    });
  }, []);

  useEffect(() => {
    if (!period) return;
    setLoading(true);
    setLiveStats({});
    setDailyStats({});
    const [y, m] = period.split('-').map(Number);
    const prevPeriod = m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
    const now = new Date();
    const isCurrentMonth = y === now.getFullYear() && m === (now.getMonth() + 1);

    Promise.all([
      api.get(`/fdm/summary?period=${period}`).then(r => r.data),
      api.get(`/partner-portal/my-branches?period=${period}`).then(r => r.data),
      api.get(`/partner-portal/my-branches?period=${prevPeriod}`).then(r => r.data).catch(() => []),
    ]).then(([s, p, prevP]) => {
      const visibleSummary = {
        ...s,
        partners_count: p.length,
        total_turnover: p.reduce((sum, item) => sum + (item.turnover || 0), 0),
        total_expected: p.reduce((sum, item) => sum + (item.total_expected || 0), 0),
        total_paid: p.reduce((sum, item) => sum + (item.total_paid || 0), 0),
        total_debt: p.reduce((sum, item) => sum + (item.debt || 0), 0),
      };
      setSummary(visibleSummary);
      setPartners(p);
      const map = {};
      prevP.forEach(pp => { map[pp.name] = pp; });
      setPrevPartnerMap(map);
    }).finally(() => setLoading(false));

    if (isCurrentMonth) {
      api.get('/fdm/live-stats', { timeout: 120000 }).then(r => {
        setLiveStats(r.data || {});
      }).catch(() => {});

      api.get('/fdm/daily-stats', { timeout: 180000 }).then(r => {
        const daily = r.data || {};
        setDailyStats(daily);
        const today = new Date().toISOString().slice(0, 10);
        const idxMap = {};
        Object.entries(daily).forEach(([sid, sd]) => {
          const days = sd.days || [];
          const ti = days.findIndex(d => d.date === today);
          idxMap[sid] = ti >= 0 ? ti : days.length - 1;
        });
        setStoreDayIdx(idxMap);
      }).catch(() => {});
    }
  }, [period]);

  useEffect(() => {
    if (!canManageAccess || activeTab !== 'access') return;
    setAccessLoading(true);
    Promise.all([
      api.get('/partner-portal/users').then(r => r.data).catch(() => []),
      api.get('/partner-portal/access').then(r => r.data).catch(() => ({})),
    ]).then(([users, access]) => {
      setPortalUsers(users);
      setBranchAccess(access || {});
    }).finally(() => setAccessLoading(false));
  }, [activeTab, canManageAccess]);

  const saveUserBranches = async (userId, storeIds) => {
    await api.put(`/partner-portal/users/${userId}/branches`, { store_external_ids: storeIds });
    setBranchAccess(prev => ({ ...prev, [String(userId)]: storeIds }));
  };

  const updatePortalUser = async (userId, patch) => {
    const current = portalUsers.find(u => u.id === userId);
    const body = { role: patch.role || current.role, active: patch.active ?? current.active };
    const token = localStorage.getItem('auth_token');
    await fetch(`/auth/users/${userId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(body),
    }).then(async r => {
      if (!r.ok) throw new Error((await r.json()).detail || 'Не удалось обновить пользователя');
      return r.json();
    });
    setPortalUsers(prev => prev.map(u => u.id === userId ? { ...u, ...body } : u));
  };

  const invoices = buildInvoices(partners);
  const unpaidCount = invoices.filter(i => i.status !== 'paid').length;

  const filteredInvoices = debtFilter === 'all'
    ? invoices
    : invoices.filter(inv => inv.status === debtFilter);

  return (
    <div>
      <div className="kp-hero">
        <Plate>Франшиза · партнёры</Plate>
        <h1 style={{ margin: 0 }}>Партнёрский портал</h1>
        <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 8 }}>
          Филиалы и расчёты с управляющей компанией
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>

        {/* ── Left navigation panel ── */}
        <div style={{
          width: 210,
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: 14,
          padding: '16px 0',
          position: 'sticky',
          top: 28,
          flexShrink: 0,
        }}>
          <div style={{ padding: '0 16px 12px', borderBottom: '1px solid var(--border)', marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Навигация
            </div>
          </div>

          {/* Period selector */}
          <div style={{ padding: '8px 12px 12px', borderBottom: '1px solid var(--border)', marginBottom: 8 }}>
            <div style={{ fontSize: 10, color: 'var(--text2)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Период
            </div>
            <select
              value={period}
              onChange={e => setPeriod(e.target.value)}
              style={{
                width: '100%', padding: '7px 10px', borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--bg3)',
                color: 'var(--text1)', fontSize: 12, cursor: 'pointer',
              }}
            >
              {periods.slice(0, 6).map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* Nav buttons */}
          <div style={{ padding: '0 8px' }}>
            {[
              { id: 'branches', icon: '🏪', label: 'Филиалы', badge: partners.length || null, badgeColor: '#00a7dc' },
              { id: 'debts', icon: '💳', label: 'Долги', badge: unpaidCount || null, badgeColor: '#e04848' },
              canManageAccess ? { id: 'access', icon: '🔐', label: 'Доступы', badge: portalUsers.length || null, badgeColor: '#0e9f6e' } : null,
              { id: 'telegram', icon: '💬', label: 'Telegram', badge: currentUser?.telegram_id ? 1 : null, badgeColor: '#00a7dc' },
            ].filter(Boolean).map(item => (
              <button
                key={item.id}
                onClick={() => setActiveTab(item.id)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: 'none', cursor: 'pointer', marginBottom: 4,
                  background: activeTab === item.id ? 'rgba(0,167,220,0.15)' : 'transparent',
                  color: activeTab === item.id ? '#00a7dc' : 'var(--text2)',
                  borderLeft: activeTab === item.id ? '3px solid #00a7dc' : '3px solid transparent',
                  transition: 'all 0.15s',
                  fontSize: 13.5, fontWeight: activeTab === item.id ? 700 : 400,
                  textAlign: 'left',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </span>
                {item.badge > 0 && (
                  <span style={{
                    background: `${item.badgeColor}25`,
                    color: item.badgeColor,
                    fontSize: 11, fontWeight: 700,
                    padding: '1px 7px', borderRadius: 10, flexShrink: 0,
                  }}>
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Summary stats */}
          {summary && !loading && (
            <div style={{ padding: '12px 14px 0', marginTop: 8, borderTop: '1px solid var(--border)' }}>
              <div style={{ fontSize: 10, color: 'var(--text2)', marginBottom: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Сводка
              </div>
              {[
                { label: 'Оборот', value: fmt(summary.total_turnover), color: '#0e9f6e' },
                { label: 'К получению', value: fmt(summary.total_expected), color: '#00a7dc' },
                { label: 'Получено', value: fmt(summary.total_paid), color: '#0e9f6e' },
                { label: 'Долг', value: fmt(summary.total_debt), color: summary.total_debt > 0 ? '#e04848' : '#0e9f6e' },
              ].map(s => (
                <div key={s.label} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 10, color: 'var(--text2)', marginBottom: 1 }}>{s.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: s.color }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Right content ── */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 80, color: 'var(--text2)' }}>⏳ Загрузка...</div>
          ) : activeTab === 'branches' ? (
            <BranchesView
              partners={partners}
              prevPartnerMap={prevPartnerMap}
              period={period}
              summary={summary}
              dailyStats={dailyStats}
              liveStats={liveStats}
              storeDayIdx={storeDayIdx}
              setStoreDayIdx={setStoreDayIdx}
            />
          ) : activeTab === 'debts' ? (
            <DebtsView
              invoices={invoices}
              filteredInvoices={filteredInvoices}
              debtFilter={debtFilter}
              setDebtFilter={setDebtFilter}
              period={period}
            />
          ) : activeTab === 'access' ? (
            <AccessView
              users={portalUsers}
              branches={partners}
              access={branchAccess}
              loading={accessLoading}
              onSave={saveUserBranches}
              onUpdateUser={updatePortalUser}
            />
          ) : (
            <TelegramLinkView currentUser={currentUser} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Access management — assign branches to portal users
───────────────────────────────────────────────────────── */
function AccessView({ users, branches, access, loading, onSave, onUpdateUser }) {
  const [draft, setDraft] = useState({});
  const getSelected = userId => draft[userId] || access[String(userId)] || [];
  const toggle = (userId, storeId) => {
    const selected = getSelected(userId);
    const next = selected.includes(storeId)
      ? selected.filter(id => id !== storeId)
      : [...selected, storeId];
    setDraft(prev => ({ ...prev, [userId]: next }));
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>Загрузка доступов...</div>;
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>🔐 Доступы к филиалам</h2>
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
          Назначай каждому пользователю только те филиалы, которые он должен видеть в партнёрском портале.
        </div>
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        {users.map(user => {
          const selected = getSelected(user.id);
          return (
            <div key={user.id} style={{
              background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 12, padding: '14px 16px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text1)' }}>
                    {user.full_name || user.username}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>
                    @{user.telegram_username || 'telegram не привязан'} · {user.role} · {user.active ? 'активен' : 'выключен'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <select
                    value={user.role}
                    onChange={e => onUpdateUser(user.id, { role: e.target.value })}
                    style={{
                      padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)',
                      background: 'var(--bg3)', color: 'var(--text1)', fontSize: 12,
                    }}
                  >
                    {['pending', 'franchise_menu', 'franchise', 'manager', 'admin'].map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                  <button
                    onClick={() => onUpdateUser(user.id, { active: user.active ? 0 : 1 })}
                    style={{
                      padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)',
                      background: user.active ? 'transparent' : '#e0484820',
                      color: user.active ? 'var(--text2)' : '#e04848',
                      cursor: 'pointer', fontSize: 12, fontWeight: 700,
                    }}
                  >
                    {user.active ? 'Выключить' : 'Включить'}
                  </button>
                  <button
                    onClick={() => onSave(user.id, selected)}
                    style={{
                      padding: '7px 14px', borderRadius: 8, border: 'none',
                      background: '#0e9f6e', color: '#fff', cursor: 'pointer',
                      fontSize: 12, fontWeight: 700,
                    }}
                  >
                    Сохранить филиалы
                  </button>
                </div>
              </div>

              {user.role === 'pending' && (
                <div style={{
                  marginBottom: 10, padding: '8px 10px', borderRadius: 8,
                  background: 'rgba(224,138,0,0.08)', border: '1px solid rgba(224,138,0,0.22)',
                  color: '#e08a00', fontSize: 12,
                }}>
                  Пользователь ожидает подтверждения. Выбери роль `franchise_menu` или `franchise`, назначь филиалы и сохрани.
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8 }}>
                {branches.map(branch => {
                  const checked = selected.includes(branch.store_external_id);
                  return (
                    <label key={branch.store_external_id} style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '8px 10px', borderRadius: 8,
                      border: `1px solid ${checked ? 'rgba(14,159,110,0.45)' : 'var(--border)'}`,
                      background: checked ? 'rgba(14,159,110,0.08)' : 'var(--bg3)',
                      cursor: 'pointer', fontSize: 12,
                    }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(user.id, branch.store_external_id)}
                      />
                      <span style={{ color: checked ? '#0e9f6e' : 'var(--text2)', fontWeight: checked ? 700 : 500 }}>
                        {branch.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Telegram linking — current portal account only
───────────────────────────────────────────────────────── */
function TelegramLinkView({ currentUser }) {
  const [botName, setBotName] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const containerId = 'partner-portal-telegram-login';

  useEffect(() => {
    fetch('/auth/config')
      .then(r => r.json())
      .then(data => setBotName(data?.telegram_bot_name || ''))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!botName) return;
    window.onPartnerPortalTelegramAuth = async (tgUser) => {
      setStatus('Привязываю Telegram...');
      setError('');
      try {
        const r = await api.post('/partner-portal/telegram/link', tgUser);
        const nextUser = { ...(getStoredUser() || currentUser || {}), ...r.data };
        localStorage.setItem('auth_user', JSON.stringify(nextUser));
        setStatus(`Telegram привязан: @${r.data.telegram_username || r.data.telegram_id}`);
      } catch (e) {
        setError(e.response?.data?.detail || 'Не удалось привязать Telegram');
        setStatus('');
      }
    };

    const container = document.getElementById(containerId);
    if (!container) return;
    setWidgetLoaded(false);
    container.innerHTML = '';
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', botName);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-request-access', 'write');
    script.setAttribute('data-onauth', 'onPartnerPortalTelegramAuth(user)');
    container.appendChild(script);
    const timer = setTimeout(() => {
      setWidgetLoaded(!!container.querySelector('iframe'));
    }, 2500);
    return () => {
      clearTimeout(timer);
      delete window.onPartnerPortalTelegramAuth;
    };
  }, [botName, currentUser]);

  return (
    <div style={{
      background: 'var(--bg2)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '20px 24px', maxWidth: 620,
    }}>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>💬 Привязка Telegram</h2>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 8, lineHeight: 1.5 }}>
        Привязка выполняется к текущему аккаунту партнёрского портала. Подтверждать через личный Telegram владельца не нужно.
      </div>

      <div style={{ marginTop: 16, padding: '12px 14px', borderRadius: 10, background: 'var(--bg3)', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 12, color: 'var(--text2)' }}>Текущий аккаунт</div>
        <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text1)', marginTop: 3 }}>
          {currentUser?.full_name || currentUser?.username || 'Пользователь'}
        </div>
        <div style={{ fontSize: 12, color: currentUser?.telegram_id ? '#0e9f6e' : '#e08a00', marginTop: 4 }}>
          {currentUser?.telegram_id
            ? `Telegram уже привязан: @${currentUser.telegram_username || currentUser.telegram_id}`
            : 'Telegram пока не привязан'}
        </div>
      </div>

      <div id={containerId} style={{ marginTop: 18, minHeight: 42 }} />
      {!botName && <div style={{ marginTop: 12, color: '#e04848', fontSize: 13 }}>Не задан TELEGRAM_BOT_NAME для Telegram Login Widget.</div>}
      {botName && !widgetLoaded && (
        <div style={{
          marginTop: 12, padding: '10px 12px', borderRadius: 8,
          background: 'rgba(224,138,0,0.08)', border: '1px solid rgba(224,138,0,0.22)',
          color: '#e08a00', fontSize: 13, lineHeight: 1.45,
        }}>
          Если кнопка Telegram не появилась, проверь в @BotFather для бота @{botName}: команда /setdomain должна быть установлена на <b>kpkerp.ru</b>. После этого обнови страницу.
        </div>
      )}
      {status && <div style={{ marginTop: 12, color: '#0e9f6e', fontSize: 13, fontWeight: 700 }}>{status}</div>}
      {error && <div style={{ marginTop: 12, color: '#e04848', fontSize: 13, fontWeight: 700 }}>{error}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Branches view — grid of partner cards (like the screenshot)
───────────────────────────────────────────────────────── */
function BranchesView({ partners, prevPartnerMap, period, summary, dailyStats, liveStats, storeDayIdx, setStoreDayIdx }) {
  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>🏪 Филиалы</h2>
        <span style={{ fontSize: 13, color: 'var(--text2)' }}>{partners.length} партнёров</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
        {partners.map((p, idx) => {
          const proj = getProjection(p.turnover, period);
          const color = COLORS[idx % COLORS.length];
          const shareOfTotal = summary?.total_turnover ? (p.turnover / summary.total_turnover * 100) : 0;
          const prev = prevPartnerMap[p.name];
          const prevTurnover = prev?.turnover || 0;
          const trendValue = proj ? proj.projection : p.turnover;
          const trendDelta = prevTurnover > 0 ? ((trendValue - prevTurnover) / prevTurnover * 100) : null;

          const sid = p.store_external_id;
          const sd = dailyStats[sid];
          const ls = liveStats[sid];
          const cashBalance = sd?.cash_balance ?? ls?.cash_balance ?? null;
          const days = sd?.days || [];
          const today = new Date().toISOString().slice(0, 10);
          const dayIdx = storeDayIdx[sid] ?? (days.length - 1);
          const day = days[dayIdx] || { date: today, revenue: 0, cash: 0, noncash: 0, count: 0 };
          const isToday = day.date === today;
          const dayLabel = day.date
            ? new Date(day.date + 'T12:00:00').toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' })
            : '';
          const canPrev = dayIdx > 0;
          const canNext = dayIdx < days.length - 1;
          const setIdx = v => setStoreDayIdx(prev => ({ ...prev, [sid]: v }));

          return (
            <div
              key={p.id}
              style={{
                background: 'var(--bg2)', borderRadius: 14, padding: '16px 20px',
                border: '1px solid var(--border)', borderLeft: `4px solid ${color}`,
                transition: 'transform 0.12s',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color, lineHeight: 1.3, flex: 1, marginRight: 8 }}>
                  {p.name}
                </div>
                <StatusBadge debt={p.debt} />
              </div>

              {/* Main metrics 2×2 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text2)' }}>Оборот</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#0e9f6e' }}>{fmt(p.turnover)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text2)' }}>Пред. месяц</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: prevTurnover > 0 ? 'var(--text1)' : 'var(--text2)' }}>
                    {prevTurnover > 0 ? fmt(prevTurnover) : '—'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text2)' }}>Платёж в УК</div>
                  <div style={{ fontSize: 15, fontWeight: 600, color: '#00a7dc' }}>{fmt(p.total_expected)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: 'var(--text2)' }}>Получено</div>
                  <div style={{
                    fontSize: 15, fontWeight: 600,
                    color: p.total_paid >= p.total_expected && p.total_expected > 0 ? '#0e9f6e' : '#e08a00',
                  }}>
                    {fmt(p.total_paid)}
                  </div>
                </div>
              </div>

              {/* Daily stats navigator */}
              {(days.length > 0 || cashBalance !== null) && (
                <div style={{
                  marginBottom: 10, padding: '10px 12px', borderRadius: 8,
                  background: 'rgba(0,167,220,0.06)', border: '1px solid rgba(0,167,220,0.2)',
                }}>
                  {/* Cash balance */}
                  {cashBalance !== null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: days.length > 0 ? 8 : 0 }}>
                      <span style={{ fontSize: 10, color: cashColor(cashBalance), textTransform: 'uppercase', fontWeight: 600 }}>
                        💰 В кассе
                      </span>
                      <span style={{ fontSize: 16, fontWeight: 800, color: cashColor(cashBalance) }}>
                        {fmtCash(cashBalance)}
                      </span>
                    </div>
                  )}

                  {days.length > 0 && (
                    <>
                      {/* Day navigator */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                        <button
                          onClick={() => canPrev && setIdx(dayIdx - 1)}
                          disabled={!canPrev}
                          style={{
                            background: canPrev ? 'rgba(0,167,220,0.15)' : 'transparent',
                            color: canPrev ? '#00a7dc' : 'var(--text2)',
                            border: '1px solid var(--border)', borderRadius: 8,
                            width: 28, height: 28, fontSize: 14,
                            cursor: canPrev ? 'pointer' : 'default',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                          }}
                        >◀</button>
                        <div style={{ textAlign: 'center', minWidth: 100 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text1)' }}>{dayLabel}</div>
                          <div style={{ fontSize: 10, color: isToday ? '#0e9f6e' : 'var(--text2)', fontWeight: 600 }}>
                            {isToday ? '● сегодня' : day.date}
                          </div>
                        </div>
                        <button
                          onClick={() => canNext && setIdx(dayIdx + 1)}
                          disabled={!canNext}
                          style={{
                            background: canNext ? 'rgba(0,167,220,0.15)' : 'transparent',
                            color: canNext ? '#00a7dc' : 'var(--text2)',
                            border: '1px solid var(--border)', borderRadius: 8,
                            width: 28, height: 28, fontSize: 14,
                            cursor: canNext ? 'pointer' : 'default',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                          }}
                        >▶</button>
                      </div>

                      {/* Revenue breakdown */}
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                        <div style={{ background: 'rgba(14,159,110,0.1)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                          <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>Выручка</div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: '#0e9f6e' }}>{fmt(day.revenue)}</div>
                        </div>
                        <div style={{ background: 'rgba(14,159,110,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                          <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>💵 Нал</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#0e9f6e' }}>{fmt(day.cash)}</div>
                        </div>
                        <div style={{ background: 'rgba(0,167,220,0.08)', borderRadius: 6, padding: '6px 8px', textAlign: 'center' }}>
                          <div style={{ fontSize: 9, color: 'var(--text2)', textTransform: 'uppercase' }}>💳 Безнал</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: '#00a7dc' }}>{fmt(day.noncash)}</div>
                        </div>
                      </div>

                      {/* Receipt count */}
                      <div style={{ textAlign: 'center', marginTop: 6, fontSize: 11, color: 'var(--text2)' }}>
                        🧾 {day.count} {day.count === 1 ? 'чек' : day.count < 5 ? 'чека' : 'чеков'}
                        {day.count > 0 && (
                          <span style={{ marginLeft: 8 }}>· ср. чек {fmt(Math.round(day.revenue / day.count))}</span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Trend */}
              {prevTurnover > 0 && trendDelta !== null && (
                <div style={{
                  marginBottom: 10, padding: '8px 12px', borderRadius: 8,
                  background: trendDelta >= 0 ? 'rgba(14,159,110,0.08)' : 'rgba(224,72,72,0.08)',
                  border: `1px solid ${trendDelta >= 0 ? 'rgba(14,159,110,0.25)' : 'rgba(224,72,72,0.25)'}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: 'var(--text2)' }}>
                      {proj ? '📈 Тренд vs пред. месяц' : 'vs пред. месяц'}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: trendDelta >= 0 ? '#0e9f6e' : '#e04848' }}>
                      {trendDelta >= 0 ? '↑' : '↓'} {Math.abs(trendDelta).toFixed(1)}%
                    </span>
                  </div>
                  {proj && (
                    <div style={{ fontSize: 10, color: 'var(--text2)', marginTop: 3 }}>
                      Прогноз {fmt(proj.projection)} vs {fmt(prevTurnover)}
                    </div>
                  )}
                </div>
              )}

              {/* Projection */}
              {proj && (
                <div style={{
                  marginBottom: 10, padding: '8px 10px', borderRadius: 8,
                  background: 'rgba(0,167,220,0.08)', border: '1px solid rgba(0,167,220,0.2)',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                    <span style={{ fontSize: 11, color: 'var(--text2)' }}>📈 Прогноз оборота на месяц</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#00a7dc' }}>{fmt(proj.projection)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text2)', marginBottom: 4 }}>
                    <span>Роялти ~{fmt(proj.projRoyalty)} · Мркт ~{fmt(proj.projMarketing)}</span>
                    <span>{fmt(Math.round(proj.dailyAvg))}/день · {proj.daysPassed}/{proj.daysInMonth} дн.</span>
                  </div>
                  <div style={{ height: 3, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${(proj.daysPassed / proj.daysInMonth) * 100}%`,
                      background: '#00a7dc', borderRadius: 2,
                    }} />
                  </div>
                </div>
              )}

              {/* Share of total */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>
                  <span>Доля в общем обороте</span>
                  <span>{shareOfTotal.toFixed(1)}%</span>
                </div>
                <div style={{ height: 4, background: 'var(--bg3)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${Math.min(shareOfTotal, 100)}%`, background: color, borderRadius: 2 }} />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Debts view — invoice list with name, amount, status
───────────────────────────────────────────────────────── */
function DebtsView({ invoices, filteredInvoices, debtFilter, setDebtFilter, period }) {
  const unpaid = invoices.filter(i => i.status === 'unpaid');
  const partial = invoices.filter(i => i.status === 'partial');
  const paid = invoices.filter(i => i.status === 'paid');
  const totalUnpaidAmount = [...unpaid, ...partial].reduce((s, i) => s + (i.amount - i.paid), 0);

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>💳 Счета и задолженности</h2>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>Период: {period}</div>
        </div>

        {/* Status filter */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { id: 'all', label: 'Все' },
            { id: 'unpaid', label: '✗ Не оплачен' },
            { id: 'partial', label: '⚠ Частично' },
            { id: 'paid', label: '✓ Оплачен' },
          ].map(f => (
            <button
              key={f.id}
              onClick={() => setDebtFilter(f.id)}
              style={{
                padding: '6px 13px', borderRadius: 8,
                border: `1px solid ${debtFilter === f.id ? 'var(--accent)' : 'var(--border)'}`,
                background: debtFilter === f.id ? 'var(--accent)' : 'var(--bg2)',
                color: debtFilter === f.id ? '#fff' : 'var(--text2)',
                cursor: 'pointer', fontSize: 12, fontWeight: debtFilter === f.id ? 600 : 400,
                transition: 'all 0.15s',
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary metric cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Всего счетов', value: invoices.length, color: 'var(--text1)', bg: 'var(--bg2)' },
          { label: 'Не оплачено', value: unpaid.length, color: '#e04848', bg: 'rgba(224,72,72,0.06)' },
          { label: 'Частично', value: partial.length, color: '#e08a00', bg: 'rgba(224,138,0,0.06)' },
          { label: 'Оплачено', value: paid.length, color: '#0e9f6e', bg: 'rgba(14,159,110,0.06)' },
        ].map(s => (
          <div
            key={s.label}
            style={{
              background: s.bg, border: '1px solid var(--border)',
              borderRadius: 10, padding: '12px 16px',
            }}
          >
            <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Outstanding debt total */}
      {totalUnpaidAmount > 0 && (
        <div style={{
          marginBottom: 18, padding: '12px 18px', borderRadius: 10,
          background: 'rgba(224,72,72,0.08)', border: '1px solid rgba(224,72,72,0.25)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: 13, color: 'var(--text2)' }}>Итого к оплате (долг + частичные)</span>
          <span style={{ fontSize: 18, fontWeight: 800, color: '#e04848' }}>{fmt(totalUnpaidAmount)}</span>
        </div>
      )}

      {/* Invoice table */}
      <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgba(0,167,220,0.06)', borderBottom: '1px solid var(--border)' }}>
              {['Филиал / Партнёр', 'Наименование', 'Сумма', 'Оплачено', 'Статус'].map(h => (
                <th
                  key={h}
                  style={{
                    padding: '10px 14px', textAlign: 'left',
                    fontSize: 11, fontWeight: 700, color: 'var(--text2)',
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((inv, i) => {
              const remaining = inv.amount - inv.paid;
              return (
                <tr
                  key={inv.id}
                  style={{
                    borderBottom: '1px solid var(--border)',
                    background: i % 2 === 0 ? 'transparent' : 'rgba(30,36,37,0.015)',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,167,220,0.05)'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : 'rgba(30,36,37,0.015)'}
                >
                  {/* Partner */}
                  <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text2)', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {inv.partner}
                  </td>

                  {/* Invoice name */}
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)' }}>{inv.name}</span>
                  </td>

                  {/* Amount */}
                  <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 700, color: 'var(--text1)', whiteSpace: 'nowrap' }}>
                    {fmt(inv.amount)}
                  </td>

                  {/* Paid */}
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <div style={{
                      fontSize: 13, fontWeight: 600,
                      color: inv.paid >= inv.amount ? '#0e9f6e' : inv.paid > 0 ? '#e08a00' : 'var(--text2)',
                    }}>
                      {fmt(inv.paid)}
                    </div>
                    {inv.status === 'partial' && (
                      <>
                        <div style={{ height: 3, width: 60, background: 'var(--bg3)', borderRadius: 2, marginTop: 3 }}>
                          <div style={{
                            height: '100%',
                            width: `${Math.min(inv.paid / inv.amount * 100, 100)}%`,
                            background: '#e08a00', borderRadius: 2,
                          }} />
                        </div>
                        <div style={{ fontSize: 10, color: '#e04848', marginTop: 2 }}>
                          остаток {fmt(remaining)}
                        </div>
                      </>
                    )}
                    {inv.status === 'unpaid' && (
                      <div style={{ fontSize: 10, color: '#e04848', marginTop: 2 }}>
                        не оплачен
                      </div>
                    )}
                  </td>

                  {/* Status badge */}
                  <td style={{ padding: '10px 14px' }}>
                    {inv.status === 'paid' && (
                      <span style={{ background: '#0e9f6e20', color: '#0e9f6e', padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        ✓ Оплачен
                      </span>
                    )}
                    {inv.status === 'partial' && (
                      <span style={{ background: '#e08a0020', color: '#e08a00', padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        ⚠ Частично
                      </span>
                    )}
                    {inv.status === 'unpaid' && (
                      <span style={{ background: '#e0484820', color: '#e04848', padding: '3px 11px', borderRadius: 20, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        ✗ Не оплачен
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}

            {!filteredInvoices.length && (
              <tr>
                <td colSpan={5} style={{ padding: 40, textAlign: 'center', color: 'var(--text2)' }}>
                  Нет данных
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Total row */}
      {filteredInvoices.length > 0 && (
        <div style={{
          marginTop: 12, padding: '12px 14px', borderRadius: 10,
          background: 'var(--bg2)', border: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end', gap: 32, flexWrap: 'wrap',
        }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--text2)' }}>Итого начислено</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text1)' }}>
              {fmt(filteredInvoices.reduce((s, i) => s + i.amount, 0))}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 11, color: 'var(--text2)' }}>Итого оплачено</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0e9f6e' }}>
              {fmt(filteredInvoices.reduce((s, i) => s + i.paid, 0))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
